import { CreditCheckResidencePayload } from '../../_data/types'
import { CertnApplicantTitle, CreditCheckResidence } from './types'

/**
 * The titles Certn accepts. `CREDIT_REPORT_1` declares `title` a required
 * input claim and constrains it to exactly these values, so the renter has to
 * pick one and a free text field would be rejected.
 */
const APPLICANT_TITLES: CertnApplicantTitle[] = [
  'Mr',
  'Mrs',
  'Miss',
  'Ms',
  'Mx',
  'Dr',
]

const CANADIAN_DIVISIONS = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
]

/**
 * The consent wording and its version.
 *
 * This text must stay character for character identical to
 * `CERTN_CONSENT_WORDING` in
 * `server/functions/src/quickpass/verifications/certn/helpers.ts`, because the
 * server stores its own copy as the renter's consent record. The version is
 * sent with the order and the server rejects a mismatch, so drift fails loudly
 * at order time rather than quietly producing a record of wording nobody saw.
 */
const CONSENT_WORDING_VERSION = '2026-08-13.1'

const CONSENT_WORDING =
  'I authorise QuickCasa to obtain a consumer credit report about me through ' +
  'Certn and its credit bureau partners, for the sole purpose of ' +
  'demonstrating my suitability as a tenant to landlords I choose to share my ' +
  'QuickPass with. I confirm that the personal information I have provided is ' +
  'accurate and describes me. I understand that QuickPass will show landlords ' +
  'only a summary of the result and never my full credit report, that I may ' +
  'withdraw this consent at any time before the check is ordered, and that I ' +
  'may request a copy of the report or dispute its contents by contacting the ' +
  'credit bureau directly.'

/**
 * What the renter pays, in cents.
 *
 * QuickCasa's own flat price, not Certn's costs passed through. The renter buys
 * one outcome, so they are shown one number: the identity check the credit
 * bureau insists on before releasing a report is ours to absorb and explain, not
 * theirs to itemise. This is the total charged, with no tax added on top.
 *
 * The server holds the authoritative copy in
 * `server/functions/src/quickpass/verifications/certn/helpers.ts` and prices the
 * PaymentIntent from it, so this value is for display only and can never be the
 * amount actually charged.
 */
const CREDIT_CHECK_PRICE_CENTS = 2000

/**
 * Formats a Canadian dollar amount for display.
 *
 * @param {number} amountInCents - the amount in cents
 * @returns {string} the formatted amount, for example CA$20.00
 */
const formatPrice = (amountInCents: number): string => {
  return `CA$${(amountInCents / 100).toFixed(2)}`
}

const POSTAL_CODE_PATTERN = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/u
const SOCIAL_INSURANCE_NUMBER_PATTERN = /^[1-79]\d{2}[ -]?\d{3}[ -]?\d{3}$/u

/**
 * Creates a blank residence entry.
 *
 * @returns {CreditCheckResidence} an empty residence
 */
const createEmptyResidence = (): CreditCheckResidence => ({
  address: {
    streetAddress: '',
    unit: '',
    locality: '',
    administrativeDivision: 'ON',
    postalCode: '',
  },
  movedInMonth: '',
  movedOutMonth: '',
})

/**
 * Converts a month input value into the ISO date Certn expects.
 *
 * The form asks for a month rather than an exact day because renters remember
 * when they moved to the month, not the date, and a guessed day is worse data
 * than a consistent first of the month.
 *
 * @param {string} monthValue - a value in YYYY-MM form
 * @returns {string} the date in YYYY-MM-DD form, or an empty string
 */
const toIsoDate = (monthValue: string): string => {
  if (monthValue === '') {
    return ''
  }

  return `${monthValue}-01`
}

/**
 * Converts the form's residence entries into the wire shape Certn expects.
 *
 * @param {CreditCheckResidence[]} residences - the entered address history
 * @returns {CreditCheckResidencePayload[]} the residences as Certn input claims
 */
const toResidencePayloads = (
  residences: CreditCheckResidence[],
): CreditCheckResidencePayload[] => {
  return residences.map(residence => ({
    address: {
      street_address: residence.address.streetAddress.trim(),
      ...(residence.address.unit.trim() === ''
        ? {}
        : { unit: residence.address.unit.trim() }),
      locality: residence.address.locality.trim(),
      administrative_division_1: residence.address.administrativeDivision,
      country: 'CA',
      postal_code: residence.address.postalCode.trim().toUpperCase(),
    },
    start_date: toIsoDate(residence.movedInMonth),
    ...(residence.movedOutMonth === ''
      ? {}
      : { end_date: toIsoDate(residence.movedOutMonth) }),
  }))
}

/**
 * Validates the form and returns the first problem found.
 *
 * @param {CertnApplicantTitle | ''} title - the selected title
 * @param {CreditCheckResidence[]} residences - the entered address history
 * @param {string} socialInsuranceNumber - the optional social insurance number
 * @param {boolean} hasGrantedConsent - whether consent was ticked
 * @returns {string} the error message, or an empty string when valid
 */
const findValidationError = (
  title: CertnApplicantTitle | '',
  residences: CreditCheckResidence[],
  socialInsuranceNumber: string,
  hasGrantedConsent: boolean,
): string => {
  if (title === '') {
    return 'Please choose a title.'
  }

  for (const [index, residence] of residences.entries()) {
    const position = index === 0 ? 'current address' : 'previous address'

    if (residence.address.streetAddress.trim() === '') {
      return `Please enter the street address for your ${position}.`
    }

    if (residence.address.locality.trim() === '') {
      return `Please enter the city for your ${position}.`
    }

    if (
      POSTAL_CODE_PATTERN.test(residence.address.postalCode.trim()) === false
    ) {
      return `Please enter a valid postal code for your ${position}.`
    }

    if (residence.movedInMonth === '') {
      return `Please tell us when you moved into your ${position}.`
    }
  }

  if (
    socialInsuranceNumber.trim() !== '' &&
    SOCIAL_INSURANCE_NUMBER_PATTERN.test(socialInsuranceNumber.trim()) === false
  ) {
    return 'That social insurance number does not look right. Leave it blank if you would rather not share it.'
  }

  if (hasGrantedConsent === false) {
    return 'We cannot run a credit check until you agree to the statement above.'
  }

  return ''
}

export {
  APPLICANT_TITLES,
  CANADIAN_DIVISIONS,
  CONSENT_WORDING,
  CONSENT_WORDING_VERSION,
  CREDIT_CHECK_PRICE_CENTS,
  formatPrice,
  createEmptyResidence,
  toIsoDate,
  toResidencePayloads,
  findValidationError,
}
